const SITE_URL = 'https://qate.ai';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Qate AI',
  url: SITE_URL,
  logo: `${SITE_URL}/og-image.png`,
  sameAs: [],
};

export const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Qate AI',
  url: SITE_URL,
};

export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Qate AI',
  applicationCategory: 'DeveloperApplication',
  operatingSystem: 'Web, Windows',
  description:
    'AI testing platform that clicks, types, and navigates your app like a real user. Conversational test creation, self-healing tests, automated bug fixing for web, Windows desktop & API.',
  url: SITE_URL,
  offers: [
    {
      '@type': 'Offer',
      name: 'Free',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Up to 3 applications, 200 credits/month',
    },
    {
      '@type': 'Offer',
      name: 'Starter',
      price: '49',
      priceCurrency: 'EUR',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '49',
        priceCurrency: 'EUR',
        billingDuration: 'P1M',
      },
      description: 'Up to 5 applications, 3,000 credits/month',
    },
    {
      '@type': 'Offer',
      name: 'Professional',
      price: '599',
      priceCurrency: 'EUR',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '599',
        priceCurrency: 'EUR',
        billingDuration: 'P1M',
      },
      description: 'Up to 10 applications, 20,000 credits/month, web + desktop',
    },
  ],
};

export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can I change plans anytime?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yeah, sure! Upgrade or downgrade whenever you like. Changes take effect immediately.',
      },
    },
    {
      '@type': 'Question',
      name: "What's included in the free plan?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The free plan includes web testing, REST API testing, SOAP service testing, CI pipeline integration, 200 credits per month plus 1,500 welcome credits, AI test failure analysis, Jira integration, GitHub and Bitbucket integration, and community support - perfect for getting started.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you offer volume discounts?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, we offer custom pricing for teams with 10+ applications or 5+ users. Contact our sales team for details.',
      },
    },
  ],
};

export const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How Qate AI Works',
  description:
    'From discovery to automated bugfixes in five simple steps',
  step: [
    {
      '@type': 'HowToStep',
      name: 'Discover',
      text: 'AI analyzes your frontend and backend repositories to understand your application structure, then actively explores your UI, discovering real user workflows.',
    },
    {
      '@type': 'HowToStep',
      name: 'Generate',
      text: 'Based on code analysis and UI exploration, Qate generates 30-40 real, executable tests covering your critical user journeys.',
    },
    {
      '@type': 'HowToStep',
      name: 'Run',
      text: 'Run tests on your infrastructure or ours. Review results through conversation, refine tests, and export as standard Playwright scripts.',
    },
    {
      '@type': 'HowToStep',
      name: 'Analyze',
      text: "When tests fail, AI analyzes the root cause - whether it's a UI change needing test healing or an actual bug in your code.",
    },
    {
      '@type': 'HowToStep',
      name: 'Fix',
      text: 'AI suggests precise bugfixes with full context - code snippets, root cause analysis, and implementation guidance. Optionally create GitHub or GitLab issues, or assign to Copilot or Duo for automated PRs.',
    },
  ],
};

export const breadcrumb = (pageName, pagePath) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: pageName,
      item: `${SITE_URL}${pagePath}`,
    },
  ],
});

export const breadcrumb3 = (parentName, parentPath, pageName, pagePath) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: parentName,
      item: `${SITE_URL}${parentPath}`,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: pageName,
      item: `${SITE_URL}${pagePath}`,
    },
  ],
});

export const articleSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: post.title,
  datePublished: post.date,
  author: {
    '@type': 'Person',
    name: post.author || 'Qate AI Team',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Qate AI',
    url: SITE_URL,
  },
  description: post.description,
});

export const webPageSchema = (page, slug) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: page.title,
  description: page.description,
  url: `${SITE_URL}/use-cases/${slug}`,
});
